import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp, DollarSign, Phone, Mail, MessageSquare,
  Plus, CheckCircle2, Zap, Award, Target, Calendar,
  Clock, User, X, Check, XCircle, Bell, Edit3, Trash2,
  Lock,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { useAppContext } from '../context/AppContext';
import { usePlan } from '../context/PlanContext';
import InsightsPanel from './InsightsPanel';
import DraggableCard from './DraggableCard';
import DashboardEditBar from './DashboardEditBar';
import { loadDashboardOrder, saveDashboardOrder, DEFAULT_ORDER } from '../utils/dashboardOrder';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const MotivationLabel = ({ pct, goal, done, label }) => {
  if (done >= goal) return <span className="mot-complete">🎉 Goal Crushed!</span>;
  if (pct >= 75)    return <span className="mot-close">🔥 {goal - done} more {label} to go!</span>;
  if (pct >= 50)    return <span className="mot-half">💪 Halfway there!</span>;
  return <span className="mot-start">{goal - done} {label} remaining</span>;
};

export default function Dashboard() {
  const {
    userData, sales, leads, bonusTiers, theme, growth,
    unitsSold, carsLeft, totalEarnings, commission, totalGross,
    paceDiff, unitsToCloseToday,
    callsToday, textsToday, emailsToday, apptsToday,
    callsPct, textsPct, emailsPct, totalGoal, totalDone, overallPct,
    currentTier, setShowAddSale, setShowQuickTouch,
    logTouch, logQuickBundle, removeTouch, setActiveTab,
    editingSaleId, setEditingSaleId, setSaleForm, setSales,
    collapsedSections, toggleSection,
    upcomingAppointments, todaysAppointments,
    completeAppointment, noShowAppointment,
    activeReminder, setActiveReminder,
    userUid,
  } = useAppContext();

  const { isPro } = usePlan();

  // Quick Add Customer modal
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickName, setQuickName] = useState('');

  // ── Drag-and-drop state ─────────────────────────────────────────────────
  const [cardOrder, setCardOrder] = useState(DEFAULT_ORDER);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  // Load saved order from Firebase on mount
  useEffect(() => {
    if (userUid) {
      loadDashboardOrder(userUid).then(order => setCardOrder(order));
    }
  }, [userUid]);

  // One-time hint tooltip
  useEffect(() => {
    const seen = localStorage.getItem('tracksy_drag_hint_seen');
    if (!seen) {
      setTimeout(() => setShowHint(true), 1500);
      setTimeout(() => {
        setShowHint(false);
        localStorage.setItem('tracksy_drag_hint_seen', '1');
      }, 5000);
    }
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 400, tolerance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 400, tolerance: 8 },
    })
  );

  const handleLongPressStart = () => {
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setIsEditMode(true);
      if (navigator.vibrate) navigator.vibrate(40);
    }, 400);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleDoneEditing = () => {
    setIsEditMode(false);
    if (userUid) {
      saveDashboardOrder(userUid, cardOrder);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = cardOrder.indexOf(active.id);
      const newIndex = cardOrder.indexOf(over.id);
      const newOrder = arrayMove(cardOrder, oldIndex, newIndex);
      setCardOrder(newOrder);
      if (userUid) {
        saveDashboardOrder(userUid, newOrder);
      }
    }
  };

  // ── Win Rate (current month leads) ────────────────────────────────────────
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthLeads = (leads || []).filter(l => l.createdAt && new Date(l.createdAt) >= monthStart);
  const soldLeads = monthLeads.filter(l => l.status === 'sold');
  const winRate = monthLeads.length > 0 ? Math.round((soldLeads.length / monthLeads.length) * 100) : 0;
  const winRateColor = winRate >= 30 ? '#00ff99' : winRate >= 20 ? '#00e5ff' : '#ffc800';

  // ── Performance Trend (last 6 months) ────────────────────────────────────
  const trendMonths = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    const label = d.toLocaleString('default', { month: 'short', year: '2-digit' });
    const units = (sales || []).filter(s => {
      const sd = new Date(s.soldDate);
      return sd.getFullYear() === d.getFullYear() && sd.getMonth() === d.getMonth();
    }).length;
    return { label, units };
  });
  const trendData = {
    labels: trendMonths.map(m => m.label),
    datasets: [{
      label: 'Units',
      data: trendMonths.map(m => m.units),
      backgroundColor: 'rgba(0,255,153,.45)',
      borderColor: '#00ff99',
      borderWidth: 1,
      borderRadius: 4,
    }],
  };
  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: 'rgba(255,255,255,.5)', font: { size: 11 } }, grid: { color: 'rgba(255,255,255,.05)' } },
      y: { ticks: { color: 'rgba(255,255,255,.5)', font: { size: 11 }, stepSize: 1 }, grid: { color: 'rgba(255,255,255,.05)' } },
    },
  };

  const handleAddCustomer = () => {
    setShowQuickAdd(true);
    setQuickName('');
  };

  const handleQuickAddSubmit = (action) => {
    const name = quickName.trim() || 'Walk-in';

    if (action === 'touch-and-lead') {
      logQuickBundle(name);
      setShowQuickAdd(false);
      setActiveTab('leads');
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('tracksy-add-lead', { detail: { customerName: name } }));
      }, 100);
    } else if (action === 'touch-only') {
      logQuickBundle(name);
      setShowQuickAdd(false);
    } else if (action === 'lead-only') {
      setShowQuickAdd(false);
      setActiveTab('leads');
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('tracksy-add-lead', { detail: { customerName: name } }));
      }, 100);
    }
  };

  const handleEditSale = (sale) => {
    setSaleForm({
      customerName: sale.customerName,
      vehicle: sale.vehicle,
      condition: sale.condition,
      frontGross: sale.frontGross,
      backGross: sale.backGross,
      spiffs: sale.spiffs,
      notes: sale.notes || '',
    });
    setEditingSaleId(sale.id);
    setShowAddSale(true);
  };

  const handleDeleteSale = (saleId) => {
    if (window.confirm('Delete this sale? This cannot be undone.')) {
      setSales(prev => prev.filter(s => s.id !== saleId));
    }
  };

  const formatApptTime = (dateStr) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return 'Unknown time';
      const now = new Date();
      const isToday = d.toDateString() === now.toDateString();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const isTomorrow = d.toDateString() === tomorrow.toDateString();

      const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      if (isToday) return `Today at ${time}`;
      if (isTomorrow) return `Tomorrow at ${time}`;
      return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${time}`;
    } catch (e) {
      return 'Unknown time';
    }
  };

  const getTimeUntil = (dateStr) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '—';
      const diff = d - new Date();
      if (diff < 0) return 'Now';
      const mins = Math.floor(diff / 60000);
      if (mins < 60) return `${mins}m`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ${mins % 60}m`;
      const days = Math.floor(hrs / 24);
      return `${days}d`;
    } catch (e) {
      return '—';
    }
  };

  // ── Card renderers ─────────────────────────────────────────────────────
  const renderCard = (cardId) => {
    switch (cardId) {

      case 'hero':
        return (
          <div className="hero-card">
            <div className="hero-shine" />
            <div className="hero-label">Month-to-Date Performance</div>
            <div className="hero-stats">
              <div className="hero-block">
                <div className="hero-number">{unitsSold}</div>
                <div className="hero-sub">Units Sold</div>
              </div>
              <div className="hero-divider" />
              <div className="hero-block">
                <div className="hero-number">${totalEarnings.toLocaleString()}</div>
                <div className="hero-sub">Est. Earnings</div>
              </div>
            </div>
            <div className="hero-bar-wrap">
              <div className="hero-bar">
                <div className="hero-bar-fill" style={{ width: `${Math.min(100,(unitsSold/userData.unitsGoal)*100)}%` }} />
              </div>
              <div className="hero-bar-label">{unitsSold} of {userData.unitsGoal} goal</div>
            </div>
          </div>
        );

      case 'growth':
        return (
          <div className="growth-card">
            <div className="growth-car-icon">
              {growth.carImage ? (
                <img src={growth.carImage} alt={growth.carName} className="growth-car-img" style={growth.carSquare ? {width:'160px',height:'160px',marginLeft:'-10px'} : {}} />
              ) : (
                <span style={{fontSize:'64px'}}>🚗</span>
              )}
            </div>
            <div className="growth-info">
              <div className="growth-label">{theme.greeting}</div>
              <div className="growth-level">{growth.carName}</div>
              <div className="growth-car-model">{growth.carDesc}</div>
              <div className="growth-bar">
                <div className="growth-bar-fill" style={{ width: `${growth.progress}%` }} />
              </div>
              <div className="growth-points">{growth.points} points • {50 - (growth.points % 50)} to upgrade</div>
            </div>
          </div>
        );

      case 'alert':
        return (
          <div className="alert-card">
            <div className="alert-icon"><Zap size={22} /></div>
            <div className="alert-body">
              <div className="alert-title">What You Need Today</div>
              <div className="alert-row">
                <div className="alert-stat">
                  <span className="alert-num">{unitsToCloseToday}</span>
                  <span className="alert-lbl">Units to Close</span>
                </div>
                <div className="alert-stat">
                  <span className="alert-num">{Math.max(0, userData.callsTarget - callsToday)}</span>
                  <span className="alert-lbl">Calls Left</span>
                </div>
                <div className="alert-stat">
                  <span className="alert-num">{Math.max(0, userData.textsTarget - textsToday)}</span>
                  <span className="alert-lbl">Texts Left</span>
                </div>
                <div className="alert-stat">
                  <span className="alert-num">{Math.max(0, userData.emailsTarget - emailsToday)}</span>
                  <span className="alert-lbl">Emails Left</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'appointments':
        if (!upcomingAppointments.length) return null;
        return (
          <div className="section-card">
            <div className="section-hdr">
              <h2><Calendar size={18} /> Upcoming Appointments</h2>
              <span className="appt-count-badge">{upcomingAppointments.length}</span>
            </div>
            <div className="appt-list">
              {upcomingAppointments.slice(0, 5).map(appt => {
                const apptDate = new Date(appt.appointmentDate);
                const isPast = apptDate < new Date();
                const isSoon = !isPast && (apptDate - new Date()) < 3600000;
                return (
                  <div key={appt.id} className={`appt-card ${isPast ? 'appt-overdue' : ''} ${isSoon ? 'appt-soon' : ''}`}>
                    <div className="appt-card-left">
                      <div className={`appt-time-badge ${isPast ? 'overdue' : isSoon ? 'soon' : ''}`}>
                        <Clock size={14} />
                        <span>{getTimeUntil(appt.appointmentDate)}</span>
                      </div>
                      <div className="appt-card-info">
                        <div className="appt-card-name">{appt.customerName}</div>
                        <div className="appt-card-details">
                          {formatApptTime(appt.appointmentDate)}
                          {appt.vehicleOfInterest && <span> · {appt.vehicleOfInterest}</span>}
                        </div>
                        {appt.notes && <div className="appt-card-notes">{appt.notes}</div>}
                      </div>
                    </div>
                    <div className="appt-card-actions">
                      <button className="appt-action-btn appt-complete-btn"
                        onClick={() => completeAppointment(appt.id)}
                        title="Mark Completed">
                        <Check size={16} />
                      </button>
                      <button className="appt-action-btn appt-noshow-btn"
                        onClick={() => noShowAppointment(appt.id)}
                        title="Mark No-Show">
                        <XCircle size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {upcomingAppointments.length > 5 && (
              <button className="btn-ghost" style={{marginTop:'8px',width:'100%',justifyContent:'center'}}
                onClick={() => setActiveTab('leads')}>
                View All {upcomingAppointments.length} Appointments
              </button>
            )}
          </div>
        );

      case 'metrics':
        return (
          <div className="metric-row">
            <div className={`metric-card ${paceDiff >= 0 ? 'positive' : 'negative'}`}>
              <TrendingUp size={20} className="metric-icon-svg" />
              <div className="metric-val">{paceDiff >= 0 ? '+' : ''}{paceDiff}</div>
              <div className="metric-lbl">Cars vs Pace</div>
              <div className="metric-detail">Sold {unitsSold} · Goal {userData.unitsGoal} · {carsLeft} left</div>
            </div>
            <div className="metric-card">
              <DollarSign size={20} className="metric-icon-svg" />
              <div className="metric-val">${commission.toLocaleString()}</div>
              <div className="metric-lbl">Commission MTD</div>
              <div className="metric-detail">${totalGross.toLocaleString()} total gross</div>
            </div>
          </div>
        );

      case 'winRate':
        return isPro ? (
          <div className="metric-card" style={{ marginBottom: 0 }}>
            <Target size={20} className="metric-icon-svg" />
            <div className="metric-val" style={{ color: winRateColor }}>
              {winRate}%
            </div>
            <div className="metric-lbl">Win Rate (MTD)</div>
            <div className="metric-detail">
              Closed {soldLeads.length} of {monthLeads.length} leads this month
            </div>
          </div>
        ) : (
          <div className="metric-card" style={{ marginBottom: 0, opacity: 0.7 }}>
            <Lock size={20} className="metric-icon-svg" style={{ color: 'rgba(255,255,255,.4)' }} />
            <div className="metric-val" style={{ color: 'rgba(255,255,255,.25)', fontSize: 18 }}>—%</div>
            <div className="metric-lbl">Win Rate</div>
            <div className="metric-detail" style={{ color: '#ffc800' }}>Available on Pro ↑</div>
          </div>
        );

      case 'trend':
        return isPro ? (
          <div className="section-card" style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 10, letterSpacing: '0.05em' }}>
              Performance Trend — Last 6 Months
            </div>
            <div style={{ height: 180 }}>
              <Bar data={trendData} options={trendOptions} />
            </div>
          </div>
        ) : (
          <div className="section-card" style={{ padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 10, letterSpacing: '0.05em' }}>
              Performance Trend
            </div>
            <div style={{ height: 180, filter: 'blur(6px)', pointerEvents: 'none', background: 'rgba(255,255,255,.03)', borderRadius: 8 }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Lock size={22} style={{ color: 'rgba(255,255,255,.4)' }} />
              <span style={{ color: '#ffc800', fontWeight: 700, fontSize: 14 }}>Available on Pro ↑</span>
            </div>
          </div>
        );

      case 'insights':
        return <InsightsPanel />;

      case 'activity':
        return (
          <div className="section-card">
            <div className="section-hdr section-hdr-toggle" onClick={() => toggleSection('dashboard-activity')}>
              <h2>🎯 Today's Activity Challenge</h2>
              <div className="hdr-right">
                <button className="btn-ghost" onClick={(e) => { e.stopPropagation(); handleAddCustomer(); }}>
                  <Plus size={16} /> Customer
                </button>
                <span className={`section-chevron ${collapsedSections['dashboard-activity'] ? 'chevron-collapsed' : ''}`}>▼</span>
              </div>
            </div>

            <div className={`section-collapse-content ${collapsedSections['dashboard-activity'] ? 'section-collapsed' : ''}`}>
              <div className="ring-row">
                <div className="ring-wrap">
                  <svg width="130" height="130">
                    <circle cx="65" cy="65" r="55" fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="11"/>
                    <circle cx="65" cy="65" r="55" fill="none"
                      stroke="url(#rg)" strokeWidth="11" strokeLinecap="round"
                      strokeDasharray={`${2*Math.PI*55}`}
                      strokeDashoffset={`${2*Math.PI*55*(1-overallPct/100)}`}
                      transform="rotate(-90 65 65)" style={{transition:'stroke-dashoffset .8s ease'}}/>
                    <defs><linearGradient id="rg" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#00ff99"/>
                      <stop offset="100%" stopColor="#00e5ff"/>
                    </linearGradient></defs>
                  </svg>
                  <div className="ring-center">
                    <div className="ring-pct">{overallPct}%</div>
                    <div className="ring-lbl">Done</div>
                  </div>
                </div>
                <div className="ring-stats">
                  <div className="ring-stat"><span className="rs-val">{totalDone}</span><span className="rs-lbl">Touches</span></div>
                  <div className="ring-stat"><span className="rs-val">{Math.max(0,totalGoal-totalDone)}</span><span className="rs-lbl">Remaining</span></div>
                  <div className="ring-stat"><span className="rs-val">{apptsToday}</span><span className="rs-lbl">Appts Today</span></div>
                </div>
              </div>

              <div className="act-bars">
                {[
                  { type:'Call',  icon:<Phone size={16}/>,         done:callsToday,  goal:userData.callsTarget,  pct:callsPct,  color:'#00ff99' },
                  { type:'Text',  icon:<MessageSquare size={16}/>,  done:textsToday,  goal:userData.textsTarget,  pct:textsPct,  color:'#00e5ff' },
                  { type:'Email', icon:<Mail size={16}/>,           done:emailsToday, goal:userData.emailsTarget, pct:emailsPct, color:'#ffc800' },
                ].map(({ type, icon, done, goal, pct, color }) => (
                  <div key={type} className={`act-bar-card ${done >= goal ? 'bar-done' : ''}`}>
                    <div className="act-bar-top">
                      <div className="act-bar-icon" style={{ background:`${color}22`, color }}>{icon}</div>
                      <span className="act-bar-name">{type}s</span>
                      <div className="act-bar-btns">
                        <button className="touch-btn touch-minus" onClick={() => removeTouch(type)}
                          disabled={done === 0} style={{ color:'#ff6b6b', borderColor:'#ff6b6b44' }}>−1</button>
                        <button className="touch-btn touch-plus" onClick={() => logTouch(type, 'Quick')}
                          style={{ color, borderColor:`${color}66` }}>+1</button>
                      </div>
                      <span className="act-bar-count" style={{ color }}>
                        {done}/{goal} {done >= goal && '✓'}
                      </span>
                    </div>
                    <div className="act-bar-track">
                      <div className="act-bar-fill" style={{ width:`${pct}%`, background:`linear-gradient(90deg, ${color}, ${color}cc)` }}>
                        {pct >= 100 && <span className="sparkle">✨</span>}
                      </div>
                    </div>
                    <div className="act-bar-mot">
                      <MotivationLabel pct={pct} goal={goal} done={done} label={type.toLowerCase() + 's'} />
                    </div>
                  </div>
                ))}

                <div className={`act-bar-card appt-bar ${apptsToday >= 1 ? 'bar-done' : ''}`}>
                  <div className="act-bar-top">
                    <div className="act-bar-icon" style={{ background:'#a855f722', color:'#a855f7' }}>
                      <Calendar size={16}/>
                    </div>
                    <span className="act-bar-name">Appointments</span>
                    <div className="act-bar-btns">
                      <button className="touch-btn touch-minus" onClick={() => removeTouch('Appointment')}
                        disabled={apptsToday === 0} style={{ color:'#ff6b6b', borderColor:'#ff6b6b44' }}>−1</button>
                      <button className="touch-btn touch-plus" onClick={() => logTouch('Appointment','Quick')}
                        style={{ color:'#a855f7', borderColor:'#a855f744' }}>+1</button>
                    </div>
                    <span className="act-bar-count" style={{ color:'#a855f7' }}>{apptsToday} today</span>
                  </div>
                </div>
              </div>

              {callsToday >= userData.callsTarget && textsToday >= userData.textsTarget && emailsToday >= userData.emailsTarget && (
                <div className="all-done-badge">
                  <span className="trophy-spin">🏆</span>
                  <div>
                    <div className="all-done-title">Daily Triple Complete!</div>
                    <div className="all-done-sub">You crushed every activity goal today</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'bonus':
        return (
          <div className="section-card">
            <div className="section-hdr section-hdr-toggle" onClick={() => toggleSection('dashboard-bonus')}>
              <h2><Award size={18} /> Bonus Tier Progress</h2>
              <span className={`section-chevron ${collapsedSections['dashboard-bonus'] ? 'chevron-collapsed' : ''}`}>▼</span>
            </div>
            <div className={`section-collapse-content ${collapsedSections['dashboard-bonus'] ? 'section-collapsed' : ''}`}>
              <div className="tier-list">
                {bonusTiers.map((tier, i) => {
                  const achieved    = unitsSold >= tier.unitsSold;
                  const isCurrent   = currentTier?.id === tier.id;
                  const isNextTarget = !achieved && !isCurrent && bonusTiers.slice(0,i).every(t => unitsSold >= t.unitsSold);
                  return (
                    <div key={tier.id} className={`tier-row ${achieved ? 'tier-achieved' : ''} ${isCurrent ? 'tier-current' : ''} ${isNextTarget ? 'tier-next' : ''}`}>
                      <div className="tier-icon">
                        {achieved ? <CheckCircle2 size={18}/> : <Target size={18}/>}
                      </div>
                      <div className="tier-info">
                        <div className="tier-units">{tier.unitsSold} Units</div>
                        <div className="tier-amount">${tier.bonusAmount.toLocaleString()} Bonus</div>
                      </div>
                      {isCurrent && <div className="tier-badge current-badge">✦ Active</div>}
                      {achieved && !isCurrent && <div className="tier-badge done-badge">Unlocked</div>}
                      {isNextTarget && <div className="tier-badge next-badge">Next Target</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'recentSales':
        return (
          <div className="section-card">
            <div className="section-hdr section-hdr-toggle" onClick={() => toggleSection('dashboard-sales')}>
              <h2>Recent Sales</h2>
              <div className="hdr-right">
                <button className="btn-add-sale" onClick={(e) => { e.stopPropagation(); setShowAddSale(true); }}>
                  <Plus size={18}/> Add Sale
                </button>
                <span className={`section-chevron ${collapsedSections['dashboard-sales'] ? 'chevron-collapsed' : ''}`}>▼</span>
              </div>
            </div>
            <div className={`section-collapse-content ${collapsedSections['dashboard-sales'] ? 'section-collapsed' : ''}`}>
              {sales.length === 0 && <div className="empty-state">No sales yet this month. Let's change that! 🚗</div>}
              <div className="sale-list">
                {[...sales].reverse().slice(0,5).map(sale => (
                  <div key={sale.id} className="sale-row">
                    <div className="sale-left">
                      <div className="sale-name">{sale.customerName}</div>
                      <div className="sale-meta">
                        <span className={`cond-badge cond-${sale.condition.toLowerCase()}`}>{sale.condition}</span>
                        <span>{sale.vehicle}</span>
                      </div>
                      {sale.notes && (
                        <div className="sale-notes">
                          <MessageSquare size={12} style={{flexShrink:0}} />
                          <span>{sale.notes}</span>
                        </div>
                      )}
                    </div>
                    <div className="sale-right">
                      <div className="sale-gross">${(sale.frontGross + sale.backGross + sale.spiffs).toLocaleString()}</div>
                      <div className="sale-date">{new Date(sale.soldDate).toLocaleDateString()}</div>
                      <div className="sale-actions">
                        <button className="sale-action-btn sale-edit-btn" onClick={() => handleEditSale(sale)} title="Edit">
                          <Edit3 size={13} />
                        </button>
                        <button className="sale-action-btn sale-delete-btn" onClick={() => handleDeleteSale(sale.id)} title="Delete">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Filter out cards that have no content (e.g. appointments when empty)
  const visibleCards = cardOrder.filter(id => {
    if (id === 'appointments') return upcomingAppointments.length > 0;
    return true;
  });

  return (
    <div className="view">

      {/* ── Appointment Reminder Banner (fixed, not reorderable) ── */}
      {activeReminder && (
        <div className="reminder-banner">
          <div className="reminder-icon"><Bell size={20} /></div>
          <div className="reminder-body">
            <div className="reminder-title">🚗 Appointment {new Date(activeReminder.appointmentDate) <= new Date() ? 'NOW!' : 'Coming Up!'}</div>
            <div className="reminder-customer">{activeReminder.customerName}</div>
            {activeReminder.vehicleOfInterest && <div className="reminder-vehicle">{activeReminder.vehicleOfInterest}</div>}
            <div className="reminder-time">{formatApptTime(activeReminder.appointmentDate)}</div>
          </div>
          <div className="reminder-actions">
            <button className="reminder-btn reminder-complete" onClick={() => completeAppointment(activeReminder.id)}>
              <Check size={16} /> Done
            </button>
            <button className="reminder-btn reminder-noshow" onClick={() => noShowAppointment(activeReminder.id)}>
              <XCircle size={16} /> No-Show
            </button>
            <button className="reminder-dismiss" onClick={() => setActiveReminder(null)}>
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── One-time drag hint ── */}
      {showHint && (
        <div style={{
          background: 'rgba(0,229,180,0.1)',
          border: '1px solid rgba(0,229,180,0.3)',
          borderRadius: '10px',
          padding: '10px 14px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          animation: 'slideUp 0.3s ease',
        }}>
          <span style={{ fontSize: '18px' }}>✦</span>
          <span style={{ fontSize: '12px', color: '#a0aab4', lineHeight: 1.4 }}>
            <strong style={{ color: '#00e5b4' }}>Tip:</strong> Long-press any card to rearrange your dashboard
          </span>
        </div>
      )}

      {/* ── Sortable card list ── */}
      <div
        onMouseDown={handleLongPressStart}
        onMouseUp={handleLongPressEnd}
        onTouchStart={handleLongPressStart}
        onTouchEnd={handleLongPressEnd}
        onTouchCancel={handleLongPressEnd}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visibleCards}
            strategy={verticalListSortingStrategy}
          >
            {visibleCards.map(cardId => (
              <DraggableCard key={cardId} id={cardId} isEditMode={isEditMode}>
                {renderCard(cardId)}
              </DraggableCard>
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <DashboardEditBar isEditMode={isEditMode} onDone={handleDoneEditing} />

      {/* ── Quick Add Customer Modal ── */}
      {showQuickAdd && (
        <div className="modal-overlay" onClick={() => setShowQuickAdd(false)}>
          <div className="modal-sheet quick-add-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <h3>➕ New Customer Touch</h3>
              <button className="modal-x" onClick={() => setShowQuickAdd(false)}><X size={20} /></button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Customer Name</label>
                <input
                  type="text"
                  value={quickName}
                  onChange={e => setQuickName(e.target.value)}
                  placeholder="Customer name (optional)"
                  autoFocus
                />
              </div>
              <div className="quick-add-info">
                <div className="quick-add-info-title">📞 3-2-1 Quick Touch logs:</div>
                <div className="quick-add-info-items">
                  <span className="quick-info-tag" style={{color:'#00ff99'}}>3 Calls</span>
                  <span className="quick-info-tag" style={{color:'#ffc800'}}>2 Emails</span>
                  <span className="quick-info-tag" style={{color:'#00e5ff'}}>1 Text</span>
                </div>
              </div>
              <div className="quick-add-buttons">
                <button className="btn-primary quick-btn-main" onClick={() => handleQuickAddSubmit('touch-and-lead')}>
                  <Zap size={16} /> Log 3-2-1 + Add Lead
                </button>
                <div className="quick-btn-row">
                  <button className="btn-secondary quick-btn-half" onClick={() => handleQuickAddSubmit('touch-only')}>
                    <Phone size={14} /> Log 3-2-1 Only
                  </button>
                  <button className="btn-secondary quick-btn-half" onClick={() => handleQuickAddSubmit('lead-only')}>
                    <User size={14} /> Add Lead Only
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
